import { useEffect, useRef } from 'react';
import { useCourseVideoPlayer } from '../../../hooks/useCourseVideoPlayer';
import { useLessonPlaybackSync } from '../../../hooks/useLessonPlaybackSync';
import { useLessonVideoPlayer } from '../../../hooks/useLessonVideoPlayer';
import { useVdoCipherPlayerEvents } from '../../../hooks/useVdoCipherPlayerEvents';
import {
  isVdoCipherLesson,
  isYouTubeLesson,
  isYouTubeUrl,
  toYouTubeEmbedUrl,
} from '../../../utils/vdocipherMappers';

function PlayerState({ title, message, actionLabel, onAction }) {
  return (
    <div className="flex aspect-video w-full flex-col items-center justify-center bg-[#111111] px-6 text-center text-white">
      {title ? <p className="text-base font-semibold sm:text-lg">{title}</p> : null}
      {message ? <p className="mt-2 max-w-md text-sm text-white/75">{message}</p> : null}
      {actionLabel && onAction ? (
        <button
          type="button"
          onClick={onAction}
          className="mt-5 rounded-full bg-[#9369CC] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#7f55b8]"
        >
          {actionLabel}
        </button>
      ) : null}
    </div>
  );
}

function YouTubePlayer({ url, title, autoplay = false, onEnded }) {
  const embedUrl = toYouTubeEmbedUrl(url);
  const src = embedUrl ? `${embedUrl}${autoplay ? '&autoplay=1' : ''}` : null;

  useEffect(() => {
    if (!onEnded) return undefined;

    const handleMessage = (event) => {
      if (event.origin !== 'https://www.youtube.com') return;

      let payload = event.data;
      if (typeof payload === 'string') {
        try {
          payload = JSON.parse(payload);
        } catch {
          return;
        }
      }

      if (payload?.event === 'onStateChange' && payload?.info === 0) {
        onEnded();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onEnded]);

  if (!src) {
    return (
      <PlayerState
        title="Video unavailable"
        message="This video does not have a valid YouTube link."
      />
    );
  }

  return (
    <div className="aspect-video w-full bg-black">
      <iframe
        title={title || 'Video'}
        src={src}
        className="h-full w-full border-0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
      />
    </div>
  );
}

function DirectVideoPlayer({
  url,
  title,
  resumeAtSeconds = 0,
  onTimeUpdate,
  onPause,
  onEnded,
  onDurationKnown,
}) {
  const videoRef = useRef(null);
  const resumeAppliedRef = useRef(false);

  useEffect(() => {
    resumeAppliedRef.current = false;
  }, [url, resumeAtSeconds]);

  if (!url) {
    return (
      <PlayerState
        title="Video unavailable"
        message="This video does not have a playable link."
      />
    );
  }

  return (
    <div className="aspect-video w-full bg-black">
      <video
        ref={videoRef}
        src={url}
        controls
        autoPlay
        playsInline
        className="h-full w-full"
        title={title || 'Video'}
        onLoadedMetadata={(event) => {
          const video = event.currentTarget;
          if (video.duration > 0) {
            onDurationKnown?.(video.duration);
          }
          if (!resumeAppliedRef.current && resumeAtSeconds > 0 && video.duration > resumeAtSeconds) {
            resumeAppliedRef.current = true;
            video.currentTime = resumeAtSeconds;
          }
        }}
        onTimeUpdate={(event) => {
          const video = event.currentTarget;
          onTimeUpdate?.(video.currentTime, video.duration);
        }}
        onPause={(event) => {
          onPause?.(event.currentTarget.currentTime);
        }}
        onEnded={(event) => {
          const video = event.currentTarget;
          onEnded?.(video.currentTime, video.duration);
        }}
      />
    </div>
  );
}

function VdoCipherPlayerFrame({
  credentials,
  resumeAtSeconds,
  onTimeUpdate,
  onPause,
  onEnded,
  onDurationKnown,
  title,
}) {
  const iframeRef = useRef(null);
  const playerSrc = `https://player.vdocipher.com/v2/?otp=${encodeURIComponent(
    credentials.otp
  )}&playbackInfo=${encodeURIComponent(credentials.playbackInfo)}`;

  useVdoCipherPlayerEvents({
    iframeRef,
    enabled: Boolean(credentials?.otp),
    resumeAtSeconds,
    onTimeUpdate,
    onPause,
    onEnded,
    onDurationKnown,
  });

  return (
    <div className="aspect-video w-full bg-black">
      <iframe
        ref={iframeRef}
        title={title || 'Video'}
        src={playerSrc}
        className="h-full w-full border-0"
        allow="encrypted-media"
        allowFullScreen
      />
    </div>
  );
}

function VdoCipherPlayback({
  credentials,
  loading,
  error,
  subscriptionRequired,
  reload,
  onRequireSubscription,
  resumeAtSeconds,
  onTimeUpdate,
  onPause,
  onEnded,
  onDurationKnown,
  title,
}) {
  if (subscriptionRequired) {
    return (
      <PlayerState
        title="Subscription required"
        message="Get an active subscription to watch this video."
        actionLabel="View plans"
        onAction={onRequireSubscription}
      />
    );
  }

  if (loading) {
    return (
      <div className="flex aspect-video w-full items-center justify-center bg-[#111111]">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-white/20 border-t-white" />
      </div>
    );
  }

  if (error) {
    return (
      <PlayerState
        title="Unable to load video"
        message={error}
        actionLabel="Try again"
        onAction={reload}
      />
    );
  }

  if (!credentials) {
    return (
      <PlayerState
        title="Video unavailable"
        message="We could not fetch secure playback credentials for this video."
        actionLabel="Try again"
        onAction={reload}
      />
    );
  }

  return (
    <VdoCipherPlayerFrame
      credentials={credentials}
      resumeAtSeconds={resumeAtSeconds}
      onTimeUpdate={onTimeUpdate}
      onPause={onPause}
      onEnded={onEnded}
      onDurationKnown={onDurationKnown}
      title={title}
    />
  );
}

export default function VideoPlayer({
  target,
  isLoggedIn = false,
  onRequireAuth,
  onRequireSubscription,
  onComplete,
  onProgressSync,
  onError,
  autoplay = false,
  emptyTitle = 'No video selected',
  emptyMessage = 'Choose a video to start watching.',
}) {
  const otpType = target?.otpType || 'lesson';
  const isLesson = otpType === 'lesson';
  const isCourse = otpType === 'course';
  const isUrl = otpType === 'url';

  const useVdocipher = !isUrl && isVdoCipherLesson(target);
  const useYoutube = isUrl ? isYouTubeUrl(target?.youtubeUrl) : isYouTubeLesson(target);
  const directUrl = isUrl && !useYoutube ? target?.videoUrl : null;

  const canPlay = isUrl
    ? true
    : Boolean(isLoggedIn || target?.isFreePreview);

  const lessonPlayer = useLessonVideoPlayer({
    lessonId: isLesson && useVdocipher ? target?.id : null,
    enabled: isLesson && useVdocipher,
    canPlay,
    onError,
  });

  const coursePlayer = useCourseVideoPlayer({
    courseId: isCourse && useVdocipher ? target?.courseId : null,
    enabled: isCourse && useVdocipher,
    canPlay,
    onError,
  });

  const vdocipherPlayer = isLesson ? lessonPlayer : coursePlayer;

  const playbackSync = useLessonPlaybackSync({
    lessonId: isLesson ? target?.id : null,
    resumeAtSeconds: isLesson ? target?.watchedSeconds : 0,
    onSynced: onProgressSync,
    onComplete,
  });

  if (!target) {
    return <PlayerState title={emptyTitle} message={emptyMessage} />;
  }

  if (!useVdocipher && !useYoutube && !directUrl) {
    return (
      <PlayerState
        title="Video unavailable"
        message="This video does not have a supported source yet."
      />
    );
  }

  if (!canPlay) {
    return (
      <PlayerState
        title="Login required"
        message="Sign in to watch this video or choose a free preview."
        actionLabel="Login to continue"
        onAction={onRequireAuth}
      />
    );
  }

  if (useYoutube) {
    return (
      <YouTubePlayer
        url={target.youtubeUrl}
        title={target.title}
        autoplay={autoplay}
        onEnded={playbackSync.handleEnded}
      />
    );
  }

  if (directUrl) {
    return (
      <DirectVideoPlayer
        url={directUrl}
        title={target.title}
        resumeAtSeconds={playbackSync.initialResumeAt}
        onTimeUpdate={playbackSync.handleTimeUpdate}
        onPause={playbackSync.handlePause}
        onEnded={playbackSync.handleEnded}
        onDurationKnown={playbackSync.handleDurationKnown}
      />
    );
  }

  return (
    <VdoCipherPlayback
      credentials={vdocipherPlayer.credentials}
      loading={vdocipherPlayer.loading}
      error={vdocipherPlayer.error}
      subscriptionRequired={vdocipherPlayer.subscriptionRequired}
      reload={vdocipherPlayer.reload}
      onRequireSubscription={onRequireSubscription}
      resumeAtSeconds={isLesson ? playbackSync.initialResumeAt : 0}
      onTimeUpdate={isLesson ? playbackSync.handleTimeUpdate : undefined}
      onPause={isLesson ? playbackSync.handlePause : undefined}
      onEnded={isLesson ? playbackSync.handleEnded : undefined}
      onDurationKnown={isLesson ? playbackSync.handleDurationKnown : undefined}
      title={target.title}
    />
  );
}
